import { useState, useRef, forwardRef, useImperativeHandle, useEffect, useContext } from "react";
import { Button, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import classnames from "classnames";
import styles from "./index.module.scss";
import { useAppCookies } from "../../assets/utils";
import { advanceNextStep, getPrecessRelatedInfo, postComments, resetProcess } from "./workflow";
import CommonDialog from "../../components/CommonDialog";
import CommonFreyrWrapper from "../../components/CommonFreyrWrapper";
import { XevmpdContext } from "../../App";

interface ProcessButtonProps {
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  beforeNextStep?: () => void;
  taskButtonConfig: API.TaskResultProperty | null;
  xevmpdId?:string;
}

export interface ProcessButtonRefFormat {
  setConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hasProcessButtons: boolean;
  assignMe: boolean;
}

const ProcessButton = forwardRef<ProcessButtonRefFormat, ProcessButtonProps>(
  (props, ref) => {
    const { setLoading, beforeNextStep, taskButtonConfig, xevmpdId } = props;
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { setTaskButtonConfig, setProcessRelatedInfo } = useContext(XevmpdContext);
    const taskAction = useRef<string>("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [comments, setComments] = useState("");
    const [isEsignaturePopupDisplay, setIsEsignaturePopupDisplay] = useState(false);
    const [relatedProcessInfo, setRelatedProcessInfo] = useState<API.PrecessRelatedInfo | null>(null);
    const commentsRef = useRef<string>(""); // Store comments in ref to avoid closure issues
    const navigate = useNavigate();
    const { userID, userName,token } = useAppCookies();


    // Store taskButtonConfig in context when it's received
    useEffect(() => {
      if (taskButtonConfig !== undefined && taskButtonConfig !== null) {
        setTaskButtonConfig(taskButtonConfig);
      }
    }, [taskButtonConfig, setTaskButtonConfig]);

    const buttonOptions = taskButtonConfig?.taskResults || [];
    const rejectButton = buttonOptions.find((i) => i.value === "Rejected");

    const assignMe = taskButtonConfig?.assignMe;
    // const assignMe = true
    
    // Calculate if process buttons are available
    const hasProcessButtons = !!assignMe && buttonOptions.length > 0;

    const handleEsignatureSuccess = () => {
      console.log("ProcessButton handleEsignatureSuccess called");
      setIsEsignaturePopupDisplay(false);
      setLoading && setLoading(true);
      setConfirmOpen(false);
      // Use the ref value which always has the latest comment
      const currentComments = commentsRef.current;
      setComments("");
      commentsRef.current = "";

      if (taskAction.current === "recall") {
        resetProcess(xevmpdId!, userName,userID, currentComments)
          .then((res: any) => {
            console.log("resetProcess response:", res);
            // Handle nested response structure (axios response.data)
            let responseCode: number | undefined;
            let responseMessage: string | undefined;
            
            if (res && typeof res === 'object') {
              if ('code' in res) {
                // Direct structure: { code, message, data }
                responseCode = res.code;
                responseMessage = res.message;
              } else if ('data' in res && res.data && typeof res.data === 'object' && 'code' in res.data) {
                // Nested structure: { data: { code, message, data } }
                responseCode = res.data.code;
                responseMessage = res.data.message;
              }
            }

            if (responseCode === 200) {
              enqueueSnackbar(t("workflow.recallStepSuccessfully"), {
                variant: "success",
                anchorOrigin: { vertical: "top", horizontal: "center" },
              });
               postComments({
                          businessModule: "xEVMPD",
                          recordId: "xEVMPD-"+xevmpdId!,
                          // tags: JSON.stringify(["Plan & Track"]),
                          createdBy: userID,
                          content: currentComments,
                          contentType: "TEXT",
                          sourceType: "Plan&Track",
                        }).then(() => {
                          console.log("Comment posted successfully");
                          setTimeout(()=>{
                              window.location.href = "/xevmpd";
                          },1000);
                        })
              setComments("");
              setTimeout(() => {
                navigate("/xevmpd");
              }, 100);
            } else {
              enqueueSnackbar(responseMessage || t("commonErrorMessage"), {
                variant: "error",
                anchorOrigin: { vertical: "top", horizontal: "center" },
              });
            }
          })
          .catch((err) => {
            console.error("Error in resetProcess:", err);
            enqueueSnackbar(err?.message || t("commonErrorMessage"), {
              variant: "error",
              anchorOrigin: { vertical: "top", horizontal: "center" },
            });
          })
          .finally(() => {
            setLoading && setLoading(false);
          });
      } else {
        advanceNextStep(xevmpdId!, userID, userName, taskAction.current, currentComments)
          .then((res: any) => {
            console.log("advanceNextStep response:", res);
            // Handle nested response structure (axios response.data)
            // Check if response has nested data structure
            let responseCode: number | undefined;
            let responseMessage: string | undefined;
            
            if (res && typeof res === 'object') {
              if ('code' in res) {
                // Direct structure: { code, message, data }
                responseCode = res.code;
                responseMessage = res.message;
              } else if ('data' in res && res.data && typeof res.data === 'object' && 'code' in res.data) {
                // Nested structure: { data: { code, message, data } }
                responseCode = res.data.code;
                responseMessage = res.data.message;
              }
            }
            
            if (responseCode === 200) {
              const message =
                taskAction.current === "Rejected"
                  ? t("workflow.rejectStepSuccessfully", { action: t(rejectButton?.label || "Rejected") })
                  : t("workflow.nextStepSuccessfully");

                postComments({
                          businessModule: "xEVMPD",
                          recordId: "xEVMPD-"+xevmpdId,
                          // tags: JSON.stringify(["Plan & Track"]),
                          createdBy: userID,
                          content: currentComments,
                          contentType: "TEXT",
                          sourceType: "Plan&Track",
                        }).then(() => {
                          
              enqueueSnackbar(message, {
                variant: "success",
                anchorOrigin: { vertical: "top", horizontal: "center" },
              });
              setComments("");
              // Clear cached process info for this record to refresh edit button state
              setTaskButtonConfig(null);
              if (xevmpdId && setProcessRelatedInfo) {
                setProcessRelatedInfo((prev: any) => {
                  const updated = { ...prev };
                  delete updated[xevmpdId];
                  return updated;
                });
              }
            });
              // Use setTimeout to ensure snackbar is shown before navigation
              setTimeout(() => {
                navigate("/xevmpd");
              }, 100);
              return;
              // Explicitly return to prevent any further execution
            } else {
              enqueueSnackbar(responseMessage || t("commonErrorMessage"), {
                variant: "error",
                anchorOrigin: { vertical: "top", horizontal: "center" },
              });
            }
          })
          .catch((err) => {
            console.error("Error in advanceNextStep:", err);
            enqueueSnackbar(err?.message || t("commonErrorMessage"), {
              variant: "error",
              anchorOrigin: { vertical: "top", horizontal: "center" },
            });
          })
          .finally(() => {
            setLoading && setLoading(false);
          });
      }
    };

    const handleCloseESign = () => {
      setIsEsignaturePopupDisplay(false);
    };

    const handleSubmitEsign = () => {
      // Additional logic before eSign if needed
      console.log("handleSubmitEsign");
    };

    const handleErrorESign = () => {
      console.log("handleErrorESign");
    };

    // Fetch related process info to check canReset - only once on mount
    useEffect(() => {
      if (xevmpdId && userID) {
        getPrecessRelatedInfo(xevmpdId, userID)
          .then((res) => {
            if (res?.data && (res.data as any).code === 200) {
              setRelatedProcessInfo((res.data as any).data);
            }
          })
          .catch((err) => {
            console.error("Error fetching related process info:", err);
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset comments when dialog opens to ensure fresh state for each attempt
    useEffect(() => {
      if (confirmOpen) {
        // Reset comments when dialog first opens
        setComments('');
        commentsRef.current = '';
      }
    }, [confirmOpen]);

    useImperativeHandle(ref, () => ({
      setConfirmOpen: setConfirmOpen,
      hasProcessButtons: hasProcessButtons,
      assignMe: assignMe || false,
    }), [hasProcessButtons, assignMe]);

   const getActionConfig = (currectAction: string) => {
    const actionConfigMap: Record<
      string,
      {
        dialogTitle: string;
        buttonName: string;
      }
    > = {
      Completed: {
        dialogTitle: t("workflow.completeTask"),
        buttonName: t("workflow.complete"),
      },
      Rejected: {
        dialogTitle: rejectButton?.label || t("workflow.completeTask"),
        buttonName: rejectButton?.label || t("workflow.complete"),
      },
      recall: {
        dialogTitle: t("workflow.recallTask"),
        buttonName: t("workflow.recall"),
      },
    };
    return (
      actionConfigMap[currectAction] || {
        dialogTitle: t("common.actionConfirm"),
        buttonName: t("workflow.complete"),
      }
    );
  };

  const dialogTitle = () => {
    return getActionConfig(taskAction.current).dialogTitle;
  };

  const primaryButtonName = () => {
    return getActionConfig(taskAction.current).buttonName;
  };
    return (
      <>
      <div>
      {/* <div className="process-button-container"> */}
      {relatedProcessInfo?.canReset ? (
          <Button
            type="button"
            size="small"
            variant="contained"
            onClick={() => {
              if (taskAction.current !== "recall") {
                setComments("");
              }
              taskAction.current = "recall";
              if (beforeNextStep) {
                beforeNextStep();
              } else {
                setConfirmOpen(true);
              }
            }}
            style={{float: 'right', marginLeft: '8px'}}
          >
            {t("workflow.recallTask")}
          </Button>
        ) : null}
        {!!assignMe && buttonOptions?.length > 0
          ? buttonOptions
              .sort((a, b) =>
                a.value === "Completed" ? -1 : b.value === "Completed" ? 1 : 0
              )
              .map((item) => {
                const isRejected = item.value === "Rejected";
                return(
                <Button
                  className={classnames({
                    [styles["reject-button"]]: item.value === "Rejected",
                  })}
                  key={item.value}
                  type="button"
                  size="small"
                  variant={item.value === "Rejected" ? "outlined" : "contained"}
                  onClick={() => {
                    if (taskAction.current !== item.value) {
                      setComments('');
                      commentsRef.current = '';
                    }
                    taskAction.current = item.value;
                    if (beforeNextStep) {
                      beforeNextStep();
                    } else {
                      setConfirmOpen(true);
                    }
                  }}
                  style={{float: 'right', marginLeft: '8px'}}
                >
                  {isRejected
                              ? item.label
                              : t(`workflow.${item.label}`)}
                </Button>)
  })
          : null}
          
          
        <CommonDialog
          handleCloseBtn={() => {
            setConfirmOpen(false);
            setComments('');
            commentsRef.current = '';
          }}
          containerStyle={styles["task-dialog"]}
          title={dialogTitle()}
          disabledPrimaryBtn={!comments.trim()}
          isDeleteDialog={false}
          openDialog={confirmOpen}
          handlePrimaryBtn={() => {
            // Store comments in ref before opening e-signature popup
            commentsRef.current = comments;
            setIsEsignaturePopupDisplay(true);
          }}
          handleSecondaryBtn={() => {
            setConfirmOpen(false);
            setComments('');
            commentsRef.current = '';
          }}
          primaryBtnName={primaryButtonName()}
          secondaryBtnName={t("cancel")}
        >
          <span
            style={{ right: "26px" }}
            className="rtq-number-tips pos-relative"
          >
            {comments ? comments.length : 0} / 500
          </span>
          <TextField
            inputProps={{ maxLength: 500 }}
            required
            label={t("comments")}
            rows={4}
            multiline
            value={comments}
            onChange={(event) => {
              const newValue = event.target.value;
              setComments(newValue);
              commentsRef.current = newValue; // Keep ref in sync
            }}
          />
        </CommonDialog>
        
        {/* ESign Wrapper - Only render when ProcessButton's e-signature is active */}
        {isEsignaturePopupDisplay && (
          <CommonFreyrWrapper
            key="process-button-esign" // Unique key to isolate from lifecycle e-signature
            token={token || ''}
            tenantID={Cookies.get("USER_TENANT_ID") || ''}
            domain={process.env.REACT_APP_STG_DOMAIN || ''}
            userName={Cookies.get("USER_NAME") || ''}
            currentLanguage={Cookies.get("USER_LANGUAGE") || 'en'}
            isEsignaturePopupDisplay={isEsignaturePopupDisplay}
            handleCloseESign={handleCloseESign}
            handleSubmitEsign={handleSubmitEsign}
            handleSuccessESign={handleEsignatureSuccess}
            handleErrorESign={handleErrorESign}
          />
        )}
        </div>
      </>
    );
  }
);

export default ProcessButton;